'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { useControlledState } from '@/hooks/useControlledState';
import { cn } from '@/lib/utils';

import Button from '../button';

interface Tab<T extends string> {
  value: T;
  label: string;
  content: React.ReactNode;
}

interface TabsProps<T extends string> {
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  tabs: Tab<T>[];
  /** Used in ARIA for accessible tabs */
  label: string;
  classNames?: {
    root?: string;
    tab?: string;
    tabContent?: string;
  };
}

export function Tabs<T extends string>({
  value: propValue,
  defaultValue,
  onValueChange,
  label,
  tabs,
  classNames,
}: TabsProps<T>) {
  const [value, setValue] = useControlledState({
    prop: propValue,
    defaultProp: defaultValue,
    onChange: onValueChange,
  });

  const [direction, setDirection] = useState(1);

  function handleTabClick(tab: T) {
    const diff = getTabIndex(tab) - getTabIndex(value);
    const absDiff = Math.abs(diff);
    setDirection(diff / absDiff);
    setValue(tab);
  }

  function getTabIndex(tab?: T) {
    if (!tab) return -1;
    return tabs.map((tab) => tab.value).indexOf(tab);
  }

  return (
    <div
      className={cn('flex h-full w-full flex-col', classNames?.root)}
      data-tracker-type='tabs'
    >
      <div className='relative shrink-0'>
        <ul role='tablist' aria-label={label} className='flex overflow-x-auto'>
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              tab={tab}
              isActive={value === tab.value}
              onClick={(tab) => handleTabClick(tab.value)}
            />
          ))}
        </ul>
        <div className='bg-neutral-N99 absolute inset-x-0 bottom-0 h-px' />
      </div>
      <AnimatePresence mode='wait'>
        <motion.div
          initial={{ x: direction * 12, opacity: 1 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: direction * 12, opacity: 0 }}
          transition={{ duration: 0.2 }}
          key={`tabpanel-${value}`}
          role='tabpanel'
          id={`tabpanel-${value}`}
          aria-labelledby={`tab-${value}`}
          className={cn(
            'h-full flex-grow overflow-y-auto pt-4',
            classNames?.tabContent
          )}
        >
          {tabs.find((tab) => tab.value === value)?.content}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function Tab<T extends string>({
  tab,
  isActive,
  classNames,
  onClick,
}: {
  tab: Tab<T>;
  isActive: boolean;
  classNames?: { tab?: string };
  onClick: (tab: Tab<T>) => void;
}) {
  return (
    <li className='relative z-[1]'>
      <Button
        noStyles
        role='tab'
        data-tracker-type='tab'
        data-tracker-context={tab.value}
        data-tab={tab.value}
        aria-selected={isActive}
        aria-controls={`tabpanel-${tab.value}`}
        className={cn(
          'text-body-l text-medium hover:text-primary-P99 focus-visible:ring-primary-P99 relative whitespace-nowrap border-none pb-4 pr-8 font-serif transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50',
          isActive && 'text-primary-P99',
          classNames?.tab
        )}
        onClick={() => onClick(tab)}
      >
        {tab.label}
      </Button>
      {isActive && (
        <motion.div
          aria-hidden
          className='bg-primary-P99 absolute bottom-0 left-0 h-px w-full'
          layoutId='underline'
        />
      )}
    </li>
  );
}
